import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import ImageUploader from '../../components/admin/ImageUploader';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import HomeContentSettings from '../../components/admin/HomeContentSettings';
import { mergeHomeContent } from '../../data/homeContent';
import PasswordInput from '../../components/PasswordInput';

const TABS = [
  { label: 'General & Branding', module: 'settings_general' },
  { label: 'Banners', module: 'settings_general' },
  { label: 'Home Sections', module: 'settings_general' },
  { label: 'SMTP / Email', module: 'settings_smtp' },
  { label: 'Payment Gateways', module: 'settings_payment' },
];

export default function AdminSettings() {
  const { refreshSettings } = useSettings();
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);

  const [smtpForm, setSmtpForm] = useState({ host: '', port: 587, secure: false, username: '', password: '', fromName: '', fromEmail: '' });
  const smtpPresets = {
    gmail: { host: 'smtp.gmail.com', port: 587, secure: false },
    outlook: { host: 'smtp.office365.com', port: 587, secure: false },
    sendgrid: { host: 'smtp.sendgrid.net', port: 587, secure: false },
    ses: { host: 'email-smtp.ap-south-1.amazonaws.com', port: 587, secure: false },
  };
  const [bannerForm, setBannerForm] = useState({ title: '', subtitle: '', buttonText: '', link: '', image: '', isActive: true, sortOrder: 0 });
  const [editingBannerId, setEditingBannerId] = useState(null);

  // Payment methods available in checkout.
  const [pmForm, setPmForm] = useState({
    cod:      { isEnabled: true, extraFee: 50, minOrderAmount: 0, maxOrderAmount: 10000, restrictedPincodesStr: '' },
    upi:      { isEnabled: false, vpa: '', merchantName: 'Chandana Aura', qrCodeImage: '' },
    bankTransfer: { isEnabled: false, bankName: '', accountName: '', accountNumber: '', ifscCode: '', branchName: '' },
    razorpay: { isEnabled: false, keyId: '', keySecret: '', webhookSecret: '' },
    stripe:   { isEnabled: false, secretKey: '', webhookSecret: '' },
    paypal:   { isEnabled: false, clientId: '', clientSecret: '', currency: 'INR' },
  });

  const fetchSettings = () => {
    api.get('/settings').then((res) => {
      setSettings({ ...res.data, homeContent: mergeHomeContent(res.data.homeContent) });
      if (res.data.smtp) {
        setSmtpForm((f) => ({
          ...f,
          host: res.data.smtp.host || '',
          port: res.data.smtp.port || 587,
          secure: res.data.smtp.secure || false,
          username: res.data.smtp.username || '',
          fromName: res.data.smtp.fromName || '',
          fromEmail: res.data.smtp.fromEmail || '',
        }));
      }
      if (res.data.paymentMethods) {
        const pm = res.data.paymentMethods;
        setPmForm((f) => ({
          cod:      { ...f.cod,      ...(pm.cod      || {}) },
          upi:      { ...f.upi,      ...(pm.upi      || {}) },
          bankTransfer: { ...f.bankTransfer, ...(pm.bankTransfer || {}) },
          razorpay: { ...f.razorpay, ...(pm.razorpay || {}) },
          stripe:   { ...f.stripe,   ...(pm.stripe   || {}) },
          paypal:   { ...f.paypal,   ...(pm.paypal   || {}) },
        }));
      }
    });
  };

  useEffect(fetchSettings, []);

  const visibleTabs = TABS
    .map((item, index) => ({ ...item, index }))
    .filter((item) => hasPerm(item.module, 'view'));
  const activeTab = visibleTabs.some((item) => item.index === tab) ? tab : visibleTabs[0]?.index;

  const saveGeneral = async (e) => {
    e.preventDefault();
    try {
      await api.put('/settings/general', { siteName: settings.siteName, tagline: settings.tagline, contact: settings.contact, theme: settings.theme });
      toast.success('General branding settings saved');
      refreshSettings();
    } catch (err) {
      toast.error('Could not save settings');
    }
  };

  const uploadLogo = async (path) => {
    setSettings((s) => ({ ...s, logo: path }));
    refreshSettings();
  };
  const uploadFavicon = async (path) => {
    setSettings((s) => ({ ...s, favicon: path }));
    refreshSettings();
  };

  const saveBanner = async () => {
    if (!bannerForm.image) {
      toast.error('Please upload a banner image');
      return;
    }
    try {
      if (editingBannerId) await api.put(`/settings/banners/${editingBannerId}`, bannerForm);
      else await api.post('/settings/banners', bannerForm, { headers: { 'Content-Type': 'application/json' } });
      toast.success(editingBannerId ? 'Banner updated successfully' : 'Banner added successfully');
      setBannerForm({ title: '', subtitle: '', buttonText: '', link: '', image: '', isActive: true, sortOrder: 0 });
      setEditingBannerId(null);
      fetchSettings();
      refreshSettings();
    } catch (err) {
      toast.error('Could not save banner');
    }
  };

  const editBanner = (banner) => {
    setEditingBannerId(banner._id);
    setBannerForm({ title: banner.title || '', subtitle: banner.subtitle || '', buttonText: banner.buttonText || '', link: banner.link || '', image: banner.image || '', isActive: banner.isActive !== false, sortOrder: banner.sortOrder || 0 });
  };

  const deleteBanner = async (id) => {
    await api.delete(`/settings/banners/${id}`);
    fetchSettings();
    refreshSettings();
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    try {
      await api.put('/settings/smtp', smtpForm);
      toast.success('SMTP settings saved');
      fetchSettings();
    } catch (err) {
      toast.error('Could not save SMTP settings');
    }
  };

  const testSmtp = async () => {
    try {
      await api.post('/settings/smtp/test');
      toast.success('SMTP connection successful');
    } catch (err) {
      toast.error(err.response?.data?.message || 'SMTP test failed');
    }
  };

  const savePaymentMethods = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put('/settings/payment-methods', { paymentMethods: pmForm });
      const sourceLabel = data.razorpay?.source === 'superadmin'
        ? 'Super Admin keys are now active'
        : data.razorpay?.source === 'environment'
          ? 'environment keys are active'
          : 'Razorpay is not configured';
      toast.success(`Payment settings updated; ${sourceLabel}`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update payment settings');
    }
  };

  if (!settings) return <div className="p-8 text-[#8A796C]">Loading configuration...</div>;

  return (
    <div className="space-y-6 text-[#2B1810] max-w-5xl">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm">
        <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Admin Control Settings</h1>
        <p className="text-xs text-[#8A796C]">Manage store branding, promo banners, SMTP email server, and popular payment gateway methods.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap bg-white p-2 rounded-xl border border-[#E8DFD8] shadow-sm">
        {visibleTabs.map((t) => (
          <button
            key={t.label}
            onClick={() => setTab(t.index)}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              activeTab === t.index ? 'bg-[#C49A45] text-white shadow' : 'bg-transparent text-[#2B1810] hover:bg-[#FAF8F5]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB 0: Branding */}
      {activeTab === 0 && (
        <form onSubmit={saveGeneral} className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <ImageUploader endpoint="/settings/logo" responseKey="logo" currentImage={settings.logo} onUploaded={uploadLogo} label="Site Logo" hint="Recommended: 1200 × 400 px (3:1). Use transparent PNG, WEBP or SVG; maximum 5 MB." />
            <ImageUploader endpoint="/settings/favicon" responseKey="favicon" currentImage={settings.favicon} onUploaded={uploadFavicon} label="Favicon Icon" hint="Recommended: 512 × 512 px (square). PNG, ICO or SVG; maximum 5 MB." />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Site Name</label>
              <input className="input-field" value={settings.siteName} onChange={(e) => setSettings({ ...settings, siteName: e.target.value })} />
            </div>

            <div>
              <label className="label">Tagline</label>
              <input className="input-field" value={settings.tagline} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Contact Email</label>
              <input className="input-field" value={settings.contact?.email || ''} onChange={(e) => setSettings({ ...settings, contact: { ...settings.contact, email: e.target.value } })} />
            </div>
            <div>
              <label className="label">Contact Phone</label>
              <input className="input-field" value={settings.contact?.phone || ''} onChange={(e) => setSettings({ ...settings, contact: { ...settings.contact, phone: e.target.value } })} />
            </div>
          </div>

          <button type="submit" className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded shadow transition-colors">
            SAVE BRANDING SETTINGS
          </button>
        </form>
      )}

      {/* TAB 1: Banners */}
      {activeTab === 1 && (
        <div className="space-y-6">
          <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="font-heading text-base font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">{editingBannerId ? 'Edit Home Banner' : 'Add New Home Banner'}</h3>
            <ImageUploader key={editingBannerId || 'new-banner'} endpoint="/uploads/image" onUploaded={(url) => setBannerForm({ ...bannerForm, image: url })} label="Banner Image" currentImage={bannerForm.image} hint="Recommended: 1920 × 900 px (wide landscape). Keep text-safe space on the left and the subject on the right. JPG or WEBP; maximum 5 MB." />
            <input placeholder="Headline Title" className="input-field" value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} />
            <input placeholder="Subtitle" className="input-field" value={bannerForm.subtitle} onChange={(e) => setBannerForm({ ...bannerForm, subtitle: e.target.value })} />
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Button Text (e.g. SHOP NOW)" className="input-field" value={bannerForm.buttonText} onChange={(e) => setBannerForm({ ...bannerForm, buttonText: e.target.value })} />
              <input placeholder="Target Link (e.g. /shop)" className="input-field" value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2"><label className="label">Display order<input type="number" className="input-field mt-1" value={bannerForm.sortOrder} onChange={(e) => setBannerForm({ ...bannerForm, sortOrder: Number(e.target.value) })}/></label><label className="flex items-center gap-2 pt-6 text-xs font-bold"><input type="checkbox" checked={bannerForm.isActive} onChange={(e) => setBannerForm({ ...bannerForm, isActive: e.target.checked })} className="h-4 w-4 accent-[#C49A45]"/>Visible on Home page</label></div>
            <div className="flex gap-3"><button type="button" onClick={saveBanner} className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-6 py-2.5 rounded shadow transition-colors">{editingBannerId ? 'UPDATE BANNER' : 'ADD BANNER'}</button>{editingBannerId && <button type="button" onClick={() => { setEditingBannerId(null); setBannerForm({ title: '', subtitle: '', buttonText: '', link: '', image: '', isActive: true, sortOrder: 0 }); }} className="border border-[#D9D0C8] px-5 py-2.5 text-xs font-bold uppercase">Cancel</button>}</div>
          </div>

          <div className="bg-white border border-[#E8DFD8] rounded-xl divide-y divide-[#E8DFD8] overflow-hidden shadow-sm">
            {settings.banners?.map((b) => (
              <div key={b._id} className="p-4 flex items-center gap-4">
                {b.image && <img src={resolveMedia(b.image)} alt="" className="w-24 h-16 object-cover rounded border border-[#E8DFD8]" />}
                <div className="flex-1">
                  <p className="font-bold text-sm text-[#2B1810]">{b.title}</p>
                  <p className="text-xs text-[#8A796C]">{b.subtitle}</p>
                  <p className="mt-1 text-[9px] font-bold uppercase text-[#9D772D]">{b.isActive ? 'Visible' : 'Hidden'} · Order {b.sortOrder || 0}</p>
                </div>
                <button type="button" onClick={() => editBanner(b)} className="border border-[#D9D0C8] px-3 py-1.5 text-xs font-bold text-[#7F5D1E]">Edit</button><button type="button" onClick={() => deleteBanner(b._id)} className="bg-red-50 hover:bg-red-100 text-red-700 font-bold text-xs py-1.5 px-3 rounded border border-red-200">Delete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: Home Sections */}
      {activeTab === 2 && <HomeContentSettings key={settings._id} initialContent={settings.homeContent} canEdit={hasPerm('settings_general', 'edit')} onSaved={(homeContent) => { setSettings((current) => ({ ...current, homeContent })); refreshSettings(); }} />}

      {/* TAB 3: SMTP */}
      {activeTab === 3 && (
        <form onSubmit={saveSmtp} className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
          <p className="text-xs text-[#8A796C]">
            Configure custom SMTP email provider (Gmail, SendGrid, Amazon SES, Mailgun). Passwords are encrypted before storage.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Email provider</label>
              <select className="input-field" value="" onChange={(e) => e.target.value && setSmtpForm({ ...smtpForm, ...smtpPresets[e.target.value] })}>
                <option value="">Choose a preset</option>
                <option value="gmail">Gmail / Google Workspace</option>
                <option value="outlook">Microsoft 365 / Outlook</option>
                <option value="sendgrid">SendGrid</option>
                <option value="ses">Amazon SES (Mumbai)</option>
              </select>
            </div>
            <div>
              <label className="label">SMTP Host</label>
              <input required placeholder="smtp.gmail.com" className="input-field" value={smtpForm.host} onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })} />
            </div>
            <div>
              <label className="label">Port</label>
              <input type="number" className="input-field" value={smtpForm.port} onChange={(e) => setSmtpForm({ ...smtpForm, port: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Username</label>
              <input className="input-field" value={smtpForm.username} onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })} />
            </div>
            <div>
              <label className="label">Password</label>
              <PasswordInput placeholder="Leave blank to keep current password" value={smtpForm.password} onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })} />
              {smtpForm.host === 'smtp.gmail.com' && <p className="mt-1 text-[10px] leading-4 text-[#8A796C]">Gmail requires a 16-character Google App Password. Do not use your normal Google password.</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">From Name</label>
              <input className="input-field" value={smtpForm.fromName} onChange={(e) => setSmtpForm({ ...smtpForm, fromName: e.target.value })} />
            </div>
            <div>
              <label className="label">From Email</label>
              <input className="input-field" value={smtpForm.fromEmail} onChange={(e) => setSmtpForm({ ...smtpForm, fromEmail: e.target.value })} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs font-bold text-[#2B1810]">
            <input type="checkbox" className="accent-[#C49A45] w-4 h-4" checked={smtpForm.secure} onChange={(e) => setSmtpForm({ ...smtpForm, secure: e.target.checked })} />
            Use SSL/TLS (Secure Port 465)
          </label>
          <p className="text-[10px] text-[#8A796C]">Enable SSL/TLS for port 465. Keep it off for port 587, which upgrades securely with STARTTLS.</p>

          <div className="flex gap-3 pt-2">
            <button className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-6 py-2.5 rounded shadow">
              SAVE SMTP SETTINGS
            </button>
            <button type="button" onClick={testSmtp} className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#C49A45] text-[#2B1810] font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded">
              TEST CONNECTION
            </button>
          </div>
        </form>
      )}

      {/* TAB 4: Payment Gateways */}
      {activeTab === 4 && (
        <form onSubmit={savePaymentMethods} className="space-y-6">

          {/* Header Banner */}
          <div className="bg-gradient-to-r from-[#2B1810] to-[#3D2517] p-5 rounded-xl text-white space-y-1">
            <h3 className="font-heading text-base font-bold">Payment Gateway Configuration</h3>
            <p className="text-xs text-[#D4C3B3]">
              Enable or disable payment methods connected to checkout. Razorpay, Stripe, and PayPal accept production credentials only.
            </p>
          </div>

          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-5 transition-all ${
            pmForm.stripe.isEnabled ? 'border-indigo-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#2B1810]">Stripe Checkout</h4>
                <p className="text-[11px] text-[#8A796C]">Hosted card and wallet checkout.</p>
                {settings.paymentRuntime && (
                  <p className="text-[10px] font-bold text-indigo-700 mt-1 uppercase">
                    {settings.paymentRuntime.stripeConfigured ? `Configured via ${settings.paymentRuntime.stripeSource}` : 'Not configured'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, stripe: { ...pmForm.stripe, isEnabled: !pmForm.stripe.isEnabled } })}
                className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors ${pmForm.stripe.isEnabled ? 'bg-indigo-500' : 'bg-gray-300'}`}
                aria-label="Enable Stripe"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${pmForm.stripe.isEnabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>
            {pmForm.stripe.isEnabled && (
              <div className="grid md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="label">Live Secret Key</label>
                  <PasswordInput value={pmForm.stripe.secretKey} onChange={(e) => setPmForm({ ...pmForm, stripe: { ...pmForm.stripe, secretKey: e.target.value } })} placeholder="sk_live_... or leave blank to keep current" />
                </div>
                <div>
                  <label className="label">Webhook Secret (Optional)</label>
                  <PasswordInput value={pmForm.stripe.webhookSecret} onChange={(e) => setPmForm({ ...pmForm, stripe: { ...pmForm.stripe, webhookSecret: e.target.value } })} placeholder="Leave blank to keep current" />
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-5 transition-all ${
            pmForm.paypal.isEnabled ? 'border-sky-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#2B1810]">PayPal Checkout</h4>
                <p className="text-[11px] text-[#8A796C]">PayPal account and supported card payments.</p>
                {settings.paymentRuntime && (
                  <p className="text-[10px] font-bold text-sky-700 mt-1 uppercase">
                    {settings.paymentRuntime.paypalConfigured ? `Configured via ${settings.paymentRuntime.paypalSource}` : 'Not configured'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, paypal: { ...pmForm.paypal, isEnabled: !pmForm.paypal.isEnabled } })}
                className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors ${pmForm.paypal.isEnabled ? 'bg-sky-500' : 'bg-gray-300'}`}
                aria-label="Enable PayPal"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${pmForm.paypal.isEnabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>
            {pmForm.paypal.isEnabled && (
              <div className="grid md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="label">Live Client ID</label>
                  <input className="input-field" value={pmForm.paypal.clientId} onChange={(e) => setPmForm({ ...pmForm, paypal: { ...pmForm.paypal, clientId: e.target.value } })} placeholder="PayPal live application Client ID" />
                </div>
                <div>
                  <label className="label">Live Client Secret</label>
                  <PasswordInput value={pmForm.paypal.clientSecret} onChange={(e) => setPmForm({ ...pmForm, paypal: { ...pmForm.paypal, clientSecret: e.target.value } })} placeholder="Leave blank to keep current secret" />
                </div>
                <div>
                  <label className="label">Currency</label>
                  <input className="input-field" value="INR" readOnly />
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-4 ${
            pmForm.cod.isEnabled ? 'border-emerald-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#2B1810]">Cash on Delivery</h4>
                <p className="text-[11px] text-[#8A796C]">Allow customers to place orders and pay at delivery.</p>
              </div>
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, cod: { ...pmForm.cod, isEnabled: !pmForm.cod.isEnabled } })}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                  pmForm.cod.isEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  pmForm.cod.isEnabled ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {pmForm.cod.isEnabled && (
              <div className="grid md:grid-cols-3 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">COD Fee</label>
                  <input type="number" className="input-field py-2.5" value={pmForm.cod.extraFee} onChange={(e) => setPmForm({ ...pmForm, cod: { ...pmForm.cod, extraFee: Number(e.target.value) } })} />
                </div>
                <div>
                  <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Minimum Order</label>
                  <input type="number" className="input-field py-2.5" value={pmForm.cod.minOrderAmount} onChange={(e) => setPmForm({ ...pmForm, cod: { ...pmForm.cod, minOrderAmount: Number(e.target.value) } })} />
                </div>
                <div>
                  <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Maximum Order</label>
                  <input type="number" className="input-field py-2.5" value={pmForm.cod.maxOrderAmount} onChange={(e) => setPmForm({ ...pmForm, cod: { ...pmForm.cod, maxOrderAmount: Number(e.target.value) } })} />
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-4 ${
            pmForm.upi.isEnabled ? 'border-purple-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#2B1810]">Direct UPI Payment</h4>
                <p className="text-[11px] text-[#8A796C]">Show UPI payment instructions on checkout.</p>
              </div>
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, upi: { ...pmForm.upi, isEnabled: !pmForm.upi.isEnabled } })}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                  pmForm.upi.isEnabled ? 'bg-purple-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  pmForm.upi.isEnabled ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {pmForm.upi.isEnabled && (
              <div className="grid md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">UPI ID / VPA</label>
                  <input className="input-field py-2.5" value={pmForm.upi.vpa} onChange={(e) => setPmForm({ ...pmForm, upi: { ...pmForm.upi, vpa: e.target.value } })} placeholder="yourstore@upi" />
                </div>
                <div>
                  <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Merchant Name</label>
                  <input className="input-field py-2.5" value={pmForm.upi.merchantName} onChange={(e) => setPmForm({ ...pmForm, upi: { ...pmForm.upi, merchantName: e.target.value } })} />
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-4 ${
            pmForm.bankTransfer.isEnabled ? 'border-slate-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-heading font-bold text-sm text-[#2B1810]">Bank Transfer</h4>
                <p className="text-[11px] text-[#8A796C]">Show bank details for NEFT / IMPS orders.</p>
              </div>
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, isEnabled: !pmForm.bankTransfer.isEnabled } })}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                  pmForm.bankTransfer.isEnabled ? 'bg-slate-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  pmForm.bankTransfer.isEnabled ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {pmForm.bankTransfer.isEnabled && (
              <div className="grid md:grid-cols-2 gap-4 text-xs">
                <input className="input-field py-2.5" value={pmForm.bankTransfer.bankName} onChange={(e) => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, bankName: e.target.value } })} placeholder="Bank Name" />
                <input className="input-field py-2.5" value={pmForm.bankTransfer.accountName} onChange={(e) => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, accountName: e.target.value } })} placeholder="Account Name" />
                <input className="input-field py-2.5" value={pmForm.bankTransfer.accountNumber} onChange={(e) => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, accountNumber: e.target.value } })} placeholder="Account Number" />
                <input className="input-field py-2.5" value={pmForm.bankTransfer.ifscCode} onChange={(e) => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, ifscCode: e.target.value } })} placeholder="IFSC Code" />
                <input className="input-field py-2.5 md:col-span-2" value={pmForm.bankTransfer.branchName} onChange={(e) => setPmForm({ ...pmForm, bankTransfer: { ...pmForm.bankTransfer, branchName: e.target.value } })} placeholder="Branch Name" />
              </div>
            )}
          </div>

          {/* ─── 1. RAZORPAY ─────────────────────────────────────────── */}
          <div className={`bg-white border-2 rounded-xl p-6 shadow-sm space-y-5 transition-all ${
            pmForm.razorpay.isEnabled ? 'border-blue-400' : 'border-[#E8DFD8]'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex flex-col items-center justify-center">
                  <span className="text-blue-700 font-black text-[10px] leading-tight">RZP</span>
                  <span className="text-blue-500 text-[8px]">pay</span>
                </div>
                <div>
                  <h4 className="font-heading font-bold text-sm text-[#2B1810]">Razorpay — Cards, UPI, Wallets, EMI</h4>
                  <p className="text-[11px] text-[#8A796C]">India's #1 payment gateway. Cards, NetBanking, UPI, GPay, Wallets, EMI.</p>
                  {settings.paymentRuntime && (
                    <p className="text-[10px] font-bold text-blue-700 mt-1 uppercase">
                      Active: {settings.paymentRuntime.razorpaySource === 'superadmin' ? 'Super Admin keys' : settings.paymentRuntime.razorpaySource === 'environment' ? 'Environment fallback' : 'Not configured'} | {settings.paymentRuntime.razorpayMode}
                    </p>
                  )}
                </div>
              </div>
              {/* Enable Toggle */}
              <button
                type="button"
                onClick={() => setPmForm({ ...pmForm, razorpay: { ...pmForm.razorpay, isEnabled: !pmForm.razorpay.isEnabled } })}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                  pmForm.razorpay.isEnabled ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  pmForm.razorpay.isEnabled ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {pmForm.razorpay.isEnabled && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Live Key ID</label>
                    <input
                      type="text"
                      value={pmForm.razorpay.keyId}
                      onChange={(e) => setPmForm({ ...pmForm, razorpay: { ...pmForm.razorpay, keyId: e.target.value } })}
                      placeholder="rzp_live_XXXXXXXXXXXX"
                      className="input-field py-2.5"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Live Key Secret</label>
                    <PasswordInput
                      value={pmForm.razorpay.keySecret}
                      onChange={(e) => setPmForm({ ...pmForm, razorpay: { ...pmForm.razorpay, keySecret: e.target.value } })}
                      placeholder="Leave blank to keep existing secret"
                      className="input-field py-2.5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-bold text-[#2B1810] uppercase tracking-wide mb-1.5">Webhook Secret (Optional)</label>
                    <PasswordInput
                      value={pmForm.razorpay.webhookSecret}
                      onChange={(e) => setPmForm({ ...pmForm, razorpay: { ...pmForm.razorpay, webhookSecret: e.target.value } })}
                      placeholder="Razorpay webhook signing secret (optional)"
                      className="input-field py-2.5"
                    />
                  </div>
                </div>

                <div className="text-[11px] px-3 py-2 rounded-lg font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Production payments are enabled only with a valid Razorpay live key pair. Real transactions will be charged to customers.
                </div>
              </div>
            )}
          </div>

          {/* Gateway Status Summary */}
          <div className="bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl p-4">
            <p className="text-xs font-bold text-[#2B1810] mb-3">Current Gateway Status — Active on Checkout:</p>
            <div className="flex gap-3 flex-wrap">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                pmForm.razorpay.isEnabled
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
              }`}>
                Razorpay {pmForm.razorpay.isEnabled ? '(Live)' : '(Off)'}
              </span>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${pmForm.stripe.isEnabled ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}>
                Stripe {pmForm.stripe.isEnabled ? '(Live)' : '(Off)'}
              </span>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${pmForm.paypal.isEnabled ? 'bg-sky-50 text-sky-700 border-sky-300' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}>
                PayPal {pmForm.paypal.isEnabled ? '(Live)' : '(Off)'}
              </span>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded-lg shadow-md transition-all hover:scale-105"
            >
              SAVE GATEWAY SETTINGS
            </button>
          </div>
        </form>
      )}

    </div>
  );
}
